import { useState, useEffect, useCallback } from 'react';

type DropdownType = 'userMenu' | 'notifications' | 'tradingOptions' | 'marketDropdown' | 'marketTickerDropdown' | 'sidebar' | 'wallet' | 'withdrawals' | null;

// Define dropdown hierarchy - parent dropdowns that should remain open when child dropdowns are opened
const DROPDOWN_HIERARCHY: Record<string, string[]> = {
  'marketTickerDropdown': ['marketDropdown'], // marketTickerDropdown is parent of marketDropdown
};

export const useDropdown = () => {
  const [activeDropdown, setActiveDropdown] = useState<DropdownType>(null);
  const [openDropdowns, setOpenDropdowns] = useState<Set<DropdownType>>(new Set());

  const openDropdown = useCallback((dropdownType: DropdownType) => {
    if (dropdownType === null) return;
    
    setOpenDropdowns(prev => {
      const newSet = new Set(prev);
      newSet.add(dropdownType);
      return newSet;
    });
    setActiveDropdown(dropdownType);
  }, []);

  const closeDropdown = useCallback((dropdownType?: DropdownType) => {
    if (dropdownType) {
      // Close specific dropdown and its children
      setOpenDropdowns(prev => {
        const newSet = new Set(prev);
        newSet.delete(dropdownType);
        
        // Also close any child dropdowns
        const children = DROPDOWN_HIERARCHY[dropdownType] || [];
        children.forEach(child => newSet.delete(child as DropdownType));
        
        return newSet;
      });
      
      // If this was the active dropdown, set active to null or another open dropdown
      if (activeDropdown === dropdownType) {
        setOpenDropdowns(prev => {
          const remainingDropdowns = Array.from(prev).filter(d => d !== dropdownType);
          setActiveDropdown(remainingDropdowns.length > 0 ? remainingDropdowns[remainingDropdowns.length - 1] : null);
          return prev;
        });
      }
    } else {
      // Close all dropdowns
      setOpenDropdowns(new Set());
      setActiveDropdown(null);
    }
  }, [activeDropdown]);

  const toggleDropdown = useCallback((dropdownType: DropdownType) => {
    if (dropdownType === null) return;
    
    setOpenDropdowns(prev => {
      const newSet = new Set(prev);
      
      if (newSet.has(dropdownType)) {
        // Close this dropdown and its children
        newSet.delete(dropdownType);
        const children = DROPDOWN_HIERARCHY[dropdownType] || [];
        children.forEach(child => newSet.delete(child as DropdownType));
        
        // If this was the active dropdown, set active to null or another open dropdown
        if (activeDropdown === dropdownType) {
          const remainingDropdowns = Array.from(newSet);
          setActiveDropdown(remainingDropdowns.length > 0 ? remainingDropdowns[remainingDropdowns.length - 1] : null);
        }
      } else {
        // Open this dropdown
        newSet.add(dropdownType);
        setActiveDropdown(dropdownType);
      }
      
      return newSet;
    });
  }, [activeDropdown]);

  // Close dropdown when clicking outside - Optimized version
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // More efficient check - only run if there are open dropdowns
      if (openDropdowns.size > 0 && !target.closest('[data-dropdown]')) {
        // Close all dropdowns when clicking outside
        setOpenDropdowns(new Set());
        setActiveDropdown(null);
      }
    };

    // Use passive listener for better performance
    document.addEventListener('mousedown', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]); // Use openDropdowns as dependency

  // Close dropdown when pressing Escape key - Optimized version
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      // Only handle escape if there are open dropdowns
      if (event.key === 'Escape' && openDropdowns.size > 0) {
        setOpenDropdowns(new Set());
        setActiveDropdown(null);
      }
    };

    document.addEventListener('keydown', handleEscape, { passive: true });
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openDropdowns]); // Use openDropdowns as dependency

  return {
    activeDropdown,
    openDropdown,
    closeDropdown,
    toggleDropdown,
    isOpen: (dropdownType: DropdownType) => openDropdowns.has(dropdownType),
  };
};
